'use strict';

import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';
import gsap from 'gsap';
import { DisplayOptions } from '../../components/displayOptions/DisplayOptions';
import { Weather } from '../../components/weather/Weather';
import {MultipleSwitch} from "../../interfaces/switchers/MultipleSwitch";
import {animateCamera} from "../animation";
import { GSAPAnimationMixer,projectMode, threeJsVariables } from '../../../settings';
import {
    getCurrentCameraDistance, getOrbitControlsCurrentPolarAngle,
    getPolarAngleFromViewMode,
    getWorldData,
    setMinAndMaxControlsDistance, setPolarAngle
} from "../../controls/orbitControls";
import { Spherical, Vector3 } from 'three';
import {Compass} from "../../components/compass/compass";
import { clamp, getDelta } from '../../../functions';
import { Lerp } from '../../projectTime/Lerp';

export class DayNightButton {
    static scope = null;
    #transitionDurationPerHour;
    #rotationCorrection;

    constructor(container) {
        this.middleState = config.features.dayNight?.middleStateButton?.type || 'sunset';
        this.switcher = new MultipleSwitch({
            buttonsDictionary: [
                {id: 'night', value: 'Night', icon: 'icon-moon'},
                {id: 'middleState', value: 'middleState', icon: 'icon-dusk'},
                {id: 'day', value: 'Day', icon: 'icon-sun'}
            ],
            changeHandlerFunction: this.#switcherHandler.bind(this),
            buttonsName: 'dayNight',
            defaultCheckedId: 'day'
        });
        this.switcher.rootElement.appendTo(container);

        this.#transitionDurationPerHour = config.features.dayNight?.transitionDuration || 0.22;
        this.#rotationCorrection = config.features.dayNight?.middleStateButton?.rotation || 0;
        this.switcherState = 'day';
        this.blockSwitcher = false;
        this.nightTime = null;
        this.toggler3D = this.switcher.rootElement.DOMElement;

        DayNightButton.scope = this;
    }

    async #switcherHandler(e) {
        if (this.blockSwitcher) {
            this.switcher.setState(`dayNight_${this.switcherState}`);
            return;
        }

        const turnOffCurrentTimeSwitcher = () => {
            const switcherRealTime = document.querySelector('.current-time__toggle');
            const togglerRealTime = switcherRealTime?.querySelector('.switch__checkbox');
            if (togglerRealTime?.checked) {
                togglerRealTime.checked = false;
                OrbitDayAndNight.scope.range.removeClass('disabled');
                OrbitDayAndNight.scope.currentTimeToggle(false);
                OrbitDayAndNight.scope.showCurrentTimeBubble();
            }
        }

        turnOffCurrentTimeSwitcher();
        const previousState = this.switcherState;
        this.switcherState = e.target.id.split('_')[1];
        this.#disableNotActiveButtons();
        switch (this.switcherState) {
            case 'night': {
                this.blockSwitcher = true;

                OrbitDayAndNight.turnOn();

                await new Promise(resolve => {
                    const interval = setInterval(() => {
                        if (OrbitDayAndNight.scope?.range) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 200);
                });

                const nightPointInSeconds = OrbitDayAndNight.scope.getDayTimeLine().sunset + 3600000;
                const nightPointInDate = new Date(nightPointInSeconds);
                const nightMinutes = nightPointInDate.getMinutes() < 10 ? `0${nightPointInDate.getMinutes()}` : nightPointInDate.getMinutes();
                const nightHours = nightPointInDate.getHours();
                const differenceBetweenPoints = Math.abs(OrbitDayAndNight.scope.range.element.value - nightPointInSeconds) / 3600000
                const duration = this.#transitionDurationPerHour * differenceBetweenPoints * (previousState === 'day' ? 2 : 1);
                const time = {value: Number(OrbitDayAndNight.scope.range.element.value)};
                gsap.to(time,{
                    value: new Date(OrbitDayAndNight.scope.currentTime).setHours(nightHours,nightMinutes),
                    duration,
                    onStart: () => {
                        this.blockSwitcher = true;
                    },
                    onUpdate: () => {
                        OrbitDayAndNight.scope.range.element.value = time.value
                        OrbitDayAndNight.scope.updateTimesOfDay(time.value, true);
                    },
                    onComplete: () => {
                        this.blockSwitcher = false;
                        this.#enableDisabledButtons();
                        this.nightTime = Number(OrbitDayAndNight.scope.range.element.value);
                    }
                });

                break;
            }
            case 'day': {
                const time = {value: Number(OrbitDayAndNight.scope.range.element.value)};
                gsap.to(time,{
                    value: new Date(OrbitDayAndNight.scope.currentTime).setHours(12,0),
                    duration: previousState === 'night' ? 3.4 : 1.7,
                    onStart: () => {
                        this.blockSwitcher = true;
                    },
                    onUpdate: () => {
                        OrbitDayAndNight.scope.range.element.value = time.value;
                        OrbitDayAndNight.scope.updateTimesOfDay(time.value, true);
                    },
                    onComplete: () => {
                        this.blockSwitcher = false;
                        this.#enableDisabledButtons();
                        if (!config.features.dayNight.checked && !DisplayOptions.isOpened) {
                            OrbitDayAndNight.turnOff();
                            Weather.scope?.resetToDefault();
                        }
                    }
                });

                break;
            }
            case 'middleState': {
                this.blockSwitcher = true;

                OrbitDayAndNight.turnOn();

                await new Promise(resolve => {
                    const interval = setInterval(() => {
                        if (OrbitDayAndNight.scope?.range) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 200);
                });

                await this.#rotateCameraTo(this.middleState);

                const middleStatePointInSeconds = this.middleState === 'sunset'
                    ? OrbitDayAndNight.scope.getDayTimeLine().sunset - 1200000
                    : OrbitDayAndNight.scope.getDayTimeLine().sunrise + 1200000;
                const middleStatePointInDate = new Date(middleStatePointInSeconds);
                const middleStateMinutes = middleStatePointInDate.getMinutes() < 10 ? `0${middleStatePointInDate.getMinutes()}` : middleStatePointInDate.getMinutes();
                const middleStateHours = middleStatePointInDate.getHours();
                const timeDifferenceBetweenPoints = Math.abs(OrbitDayAndNight.scope.range.element.value - middleStatePointInSeconds) / 3600000;
                const duration = this.#transitionDurationPerHour * timeDifferenceBetweenPoints;
                const time = {value: Number(OrbitDayAndNight.scope.range.element.value)};
                gsap.to(time,{
                    value: new Date(OrbitDayAndNight.scope.currentTime).setHours(middleStateHours, middleStateMinutes),
                    duration,
                    // delay,
                    onStart: () => {
                        this.blockSwitcher = true;
                    },
                    onUpdate: () => {
                        OrbitDayAndNight.scope.range.element.value = time.value;
                        OrbitDayAndNight.scope.updateTimesOfDay(time.value, true);
                    },
                    onComplete: () => {
                        this.blockSwitcher = false;
                        this.#enableDisabledButtons();
                        this.nightTime = Number(OrbitDayAndNight.scope.range.element.value);
                    }
                });
                break;
            }
        }
    }

    setSwitcherState(targetState, dayInfo) {
        if (this.blockSwitcher)
            return;

        const sunriseTime = dayInfo.sunrise;
        const sunsetTime = dayInfo.sunset;

        const nightState = targetState < sunriseTime || targetState > sunsetTime;
        const dayState = targetState > sunriseTime + 1800000 && targetState < sunsetTime - 1800000;
        const middleState = this.middleState === 'sunset'
            ? targetState < sunsetTime && targetState > sunsetTime - 1800000
            : targetState > sunriseTime && targetState < sunriseTime + 1800000;

        let state;
        if (nightState) {
            state = 'night';
        } else if (dayState) {
            state = 'day';
        } else if (middleState) {
            state = 'middleState';
        }

        if (state && state !== this.switcherState) {
            this.switcherState = state;
            this.switcher.setState(`dayNight_${this.switcherState}`);
        }
    }

    resetToDefault() {
        if (!DayNightButton.canActivate())
            return;

        const time = Number(OrbitDayAndNight.scope?.range?.element.value);
        if (this.switcherState !== 'day' && time === this.nightTime) {
            const value = new Date(OrbitDayAndNight.scope.currentTime).setHours(12,0);
            OrbitDayAndNight.scope.range.element.value = value;
            OrbitDayAndNight.scope.updateTimesOfDay(value, true);
        }
        this.switcherState = 'day';
        this.blockSwitcher = false;
        this.#enableDisabledButtons();
    }

    #rotateCameraTo(exposure) {
        return new Promise (resolve => {
            if (projectMode.AirMode.active)
                return resolve();
            
            const targetPolarAngle = 1.15;
            let exposureDirection = 0;
            
            switch (exposure) {
                case 'sunset':
                    exposureDirection = Math.PI / 2;
                    break;
                case 'sunrise':
                    exposureDirection = -Math.PI / 2;
                    break;
            }
            
            // const rotationCorrection = exposure === 'sunset' ? Math.PI / (2 - this.#rotationCorrection) : 3 * Math.PI / (2 - this.#rotationCorrection);
    
            const currentPolar = threeJsVariables.orbitControls.getPolarAngle();
            const currentTheta = threeJsVariables.orbitControls.getAzimuthalAngle();
            const targetTheta = config.threejs.compass.correction + exposureDirection + this.#rotationCorrection;
            
            const start = new Vector3().setFromSphericalCoords(1,Math.PI / 2,currentTheta);
            const end = new Vector3().setFromSphericalCoords(1,Math.PI / 2,targetTheta);
            let dot = clamp(start.dot(end),0,1);
            const isSameVector = dot > 0.7;
            const duration = clamp(GSAPAnimationMixer.duration * (1 - dot),0.35,GSAPAnimationMixer.duration);

            if (isSameVector)
                resolve(isSameVector);
            
            const deltaTheta = getDelta(currentTheta,targetTheta);
            const thetaLerp = new Lerp(currentTheta,currentTheta + deltaTheta);
            const phiLerp = new Lerp(currentPolar,targetPolarAngle);
            
            const progress = {value: 0};
            
            gsap.to(progress,{
                value: 1,
                ease: GSAPAnimationMixer.ease,
                duration: duration,
                onUpdate: () => {
                    threeJsVariables.orbitControls.setAzimuthAngle(thetaLerp.lerp(progress.value));
                    threeJsVariables.orbitControls.setPolarAngle(phiLerp.lerp(progress.value));
                },
                onComplete: () => resolve(isSameVector)
            });
        });
    }

    #disableNotActiveButtons() {
        const notCheckedButtons = [...document.querySelectorAll('.multiple-switch__radio-button[name="dayNight"]:not(:checked)')];
        notCheckedButtons.forEach(button => {
            button.setAttribute('disabled', 'disabled');
        });
    }

    #enableDisabledButtons() {
        const disabledButtons = [...document.querySelectorAll('.multiple-switch__radio-button[name="dayNight"][disabled]')];
        disabledButtons.forEach(button => {
            button.removeAttribute('disabled');
        });
    }

    // updatePreviousState(action) {
    //     switch(action) {
    //         case 'show':
    //             this.prevState = true;
    //             break;
    //     }
    // }

    static canActivate() {
        return (OrbitDayAndNight.isActive && !projectMode.AirMode.active) || (projectMode.AirMode.active && OrbitDayAndNight.isActive && projectMode.AirMode.hasDayAndNight())
            // && Environment.fromBundle()
    }

}
